/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { Carousel, SearchScreen } from "client-code/smart-search";

import type { TCarouselRail } from "../../model/types";
import {
  isSmartSearchChannelRail,
  mapSmartSearchCarouselToRail,
  mapSmartSearchFeedItem
} from "../utility/smartSearchCarouselMapper";

/** Maximum query length accepted by the search input (PRD §2.2.5). */
const MAX_QUERY_LENGTH: number = 100;

/**
 * Fallback tile template when the server omits `template` on a carousel.
 * The generated schema now supplies a per-carousel `template`, so this is
 * only used defensively; POSTER_HORIZONTAL is the design default for
 * search result rails.
 */
/**
 * Trims, collapses internal whitespace, lowercases, and caps a raw search
 * query at MAX_QUERY_LENGTH before it is sent to PCD (PRD §2.6.3).
 */
export const normalizeSearchQuery = (raw: string): string =>
  raw.trim().replace(/\s+/g, " ").toLowerCase().slice(0, MAX_QUERY_LENGTH);

/**
 * Maps a search FeedItemType to the app's TContentType, used to select
 * which TilePreview fields apply to a given tile and whether the tile is
 * rendered as a channel/provider logo.
 */
/**
 * Resolves the tile template for a carousel. The server now supplies
 * `template` per carousel (its string values match the app CarouselTemplate
 * enum 1:1), so it is used directly; the DEFAULT_SEARCH_TEMPLATE is only a
 * defensive fallback for older/absent values.
 */
/**
 * Whether a `carousel.feedItems` entry is the generated schema's nested
 * `FeedItems` wrapper (`{resolvedCount?, feedItems: FeedItem[]}`) rather than
 * a `FeedItem` itself.
 */
/**
 * Flattens a carousel's `feedItems` array into a single ordered FeedItem[],
 * filtering out Promotion items (not in the PRD's category list, not present
 * in Figma v5.0).
 *
 * The generated `Carousel.feedItems` type is `Array<FeedItems>`
 * (each entry a `{resolvedCount?, feedItems: FeedItem[]}` wrapper), but the
 * live smart-search service actually returns each entry as a `FeedItem`
 * directly — a flat array, no wrapper. `isFeedItemsWrapper` detects which
 * shape a given entry actually is so both are handled correctly.
 */
/**
 * Whether every item in a rendered rail is a channel/streaming-provider
 * logo tile — used by SearchResults to pass showChannelLogo to Carousel.
 * Carousel identity is server-assigned (carouselName), so this is derived
 * from item content types rather than a fixed rail id.
 */
export const isChannelRail = isSmartSearchChannelRail;

/**
 * Maps a single smart-search FeedItem to the app's TCarouselItem, resolving
 * artwork from the opaque digItemId via the shared ImageUrlGenerator — the
 * same field mapping homescreenMappers.mapFeedItemToCarouselItem uses, since
 * the smart-search FeedItem now carries the full rich field set (stationId,
 * airing times, season/episode, rating, live/progress flags).
 */
export const mapFeedItemToSearchCarouselItem = mapSmartSearchFeedItem;

/**
 * Maps one server carousel to a TCarouselRail: derives the template from the
 * server (falling back to POSTER_HORIZONTAL), flattens the nested feedItems
 * wrappers, filters Promotion items, and maps each item.
 */
/**
 * Maps the generated grouped SearchScreen into ordered TCarouselRails.
 * The server groups and captions results and supplies each carousel's tile
 * template; the client only maps items and drops carousels left empty (e.g.
 * a Promotion-only carousel, or one the server returned with no items).
 */
export const mapSmartSearchScreenResponseToCarousels = (
  response: SearchScreen,
  imageHostBaseUrl: string
): TCarouselRail[] => {
  const carousels: Carousel[] = response.carousels || [];
  const rails: TCarouselRail[] = [];
  for (const carousel of carousels) {
    const rail: TCarouselRail = mapSmartSearchCarouselToRail(
      carousel,
      imageHostBaseUrl
    );
    if (rail.items.length > 0) {
      rails.push(rail);
    }
  }
  return rails;
};

/** Total item count across all rendered rails, used to gate success/noResults. */
export const countCarouselItems = (carousels: TCarouselRail[]): number =>
  carousels.reduce(
    (total: number, rail: TCarouselRail): number => total + rail.items.length,
    0
  );

/**
 * Category to attach to the searchQuery analytics event. Prefer the first
 * mapped item categoryLabel; fall back to the first rail title, then "search".
 */
export const extractSearchQueryCategory = (
  carousels: TCarouselRail[]
): string => {
  for (const rail of carousels) {
    for (const item of rail.items) {
      if (item.categoryLabel.length > 0) {
        return item.categoryLabel;
      }
    }
    if (rail.title.length > 0) {
      return rail.title;
    }
  }
  return "search";
};

/**
 * The server-issued pageCursor for the next page of carousels, or null when
 * the response has no further pages. Mirrors homescreenMappers' cursor
 * handling so "load more carousels" behaves identically across screens.
 */
export const extractScreenPageCursor = (
  response: SearchScreen
): string | null =>
  response.pageCursor !== undefined &&
  response.pageCursor !== null &&
  response.pageCursor.length > 0
    ? response.pageCursor
    : null;
